import { useState, useEffect, useCallback } from "react";
import type { Session } from "../api/schemas";
import * as api from "../api/client";
import { SessionCard } from "./SessionCard";
import { CreateSessionForm } from "./CreateSessionForm";

export function SessionsPage() {
    const [sessions, setSessions] = useState<Session[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadSessions = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await api.listSessions();
            setSessions(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load sessions");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSessions();
    }, [loadSessions]);

    const handleCreate = useCallback(async (name: string) => {
        await api.createSession({ name });
    }, []);

    const handleDelete = useCallback((id: string) => {
        setSessions((prev) => prev.filter((s) => s.id !== id));
    }, []);

    const handleUpdate = useCallback((updated: Session) => {
        setSessions((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
    }, []);

    if (loading && sessions.length === 0) {
        return (
            <div className="w-full">
                <p className="text-center p-8 bg-bg rounded-xl my-4">Loading sessions...</p>
            </div>
        );
    }

    return (
        <div className="w-full">
            <header className="text-center mb-8">
                <h1 className="text-4xl font-bold mb-2">🏋️ Gym Sessions</h1>
                <p className="opacity-70">Track your workout sessions with precision</p>
            </header>

            <CreateSessionForm onCreated={loadSessions} onCreate={handleCreate} />

            {error && (
                <p className="text-center p-8 bg-bg rounded-xl my-4 text-stop border border-stop">
                    {error}
                </p>
            )}

            <div className="flex flex-col gap-4">
                {sessions.length === 0 && !loading && (
                    <p className="text-center p-8 bg-bg rounded-xl my-4">
                        No sessions yet. Create your first workout session above!
                    </p>
                )}
                {sessions.map((session) => (
                    <SessionCard
                        key={session.id}
                        session={session}
                        onDelete={handleDelete}
                        onUpdate={handleUpdate}
                    />
                ))}
            </div>
        </div>
    );
}
